import { supabase } from '../config/supabase';

/**
 * Supabase Authentication Service
 * Handles all auth operations for Alpha01
 */
export const supabaseAuthService = {
  /**
   * Sign up a new user
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @param {object} userData - Additional user data (firstName, lastName, role, etc.)
   */
  async signUp(email, password, userData = {}) {
    try {
      // Sign up user - database trigger will automatically create profile
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            firstName: userData.firstName,
            lastName: userData.lastName,
            role: userData.role || 'patient',
            tenantId: userData.tenantId
          }
        }
      });

      if (error) throw error;

      // Profile is automatically created by database trigger (handle_new_user)
      // No need to manually create it here!
      
      // If role-specific data provided, create role records
      if (data.user && userData.profileData) {
        if (userData.role === 'physician' && userData.profileData.medical_license_number) {
          // Wait a moment for trigger to complete
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const { error: physError } = await supabase.from('physicians').insert({
            user_id: data.user.id,
            medical_license_number: userData.profileData.medical_license_number,
            specialty: userData.profileData.specialty,
            years_experience: userData.profileData.years_experience || null,
            status: 'pending'
          });
          
          if (physError) {
            console.warn('Note: Physician record creation will be handled by admin approval:', physError.message);
          }
        } else if (userData.role === 'sponsor') {
          // Wait a moment for trigger to complete
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const { error: sponsorError } = await supabase.from('sponsors').insert({
            user_id: data.user.id,
            organization_name: userData.profileData.organization_name || null,
            status: 'pending'
          });
          
          if (sponsorError) {
            console.warn('Note: Sponsor record creation will be handled by admin approval:', sponsorError.message);
          }
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
  },

  /**
   * Sign in with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   */
  async signIn(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Get user profile from database
      if (data.user) {
        const profile = await this.getUserProfile(data.user.id);
        return { 
          data: { ...data, profile }, 
          error: null 
        };
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  },

  /**
   * Sign out current user
   */
  async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      return { error };
    }
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      
      if (user) {
        const profile = await this.getUserProfile(user.id);
        return { user: { ...user, profile }, error: null };
      }
      
      return { user: null, error: null };
    } catch (error) {
      console.error('Get current user error:', error);
      return { user: null, error };
    }
  },

  /**
   * Get user profile from database
   * @param {string} userId - User's ID
   */
  async getUserProfile(userId) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Get user profile error:', error);
      return null;
    }
  },

  /**
   * Create user profile in database
   * @param {string} userId - User's ID from auth
   * @param {object} profileData - Profile data
   */
  async createUserProfile(userId, profileData) {
    try {
      if (import.meta.env.DEV) console.log('Creating profile for user:', userId);
      
      // Insert into users table
      const { data, error } = await supabase
        .from('users')
        .insert({
          id: userId,
          email: profileData.email,
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          tenant_id: profileData.tenant_id,
          tenant_role: profileData.tenant_role,
          approval_status: 'pending',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error inserting user profile:', error);
        throw error;
      }

      if (import.meta.env.DEV) console.log('Profile created successfully for user:', userId);

      // Create role-specific record if needed
      if (profileData.tenant_role === 'physician' && profileData.medical_license_number) {
        const { error: physError } = await supabase.from('physicians').insert({
          user_id: userId,
          medical_license_number: profileData.medical_license_number,
          specialty: profileData.specialty,
          years_experience: profileData.years_experience || null,
          status: 'pending',
          created_at: new Date().toISOString()
        });
        
        if (physError) {
          console.error('Error creating physician record:', physError);
          // Don't throw here, profile was created successfully
        }
      } else if (profileData.tenant_role === 'sponsor') {
        const { error: sponsorError } = await supabase.from('sponsors').insert({
          user_id: userId,
          organization_name: profileData.organization_name || null,
          status: 'pending',
          created_at: new Date().toISOString()
        });
        
        if (sponsorError) {
          console.error('Error creating sponsor record:', sponsorError);
          // Don't throw here, profile was created successfully
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Create user profile error:', error);
      return { data: null, error };
    }
  },

  /**
   * Update user profile
   * @param {string} userId - User's ID
   * @param {object} updates - Profile updates
   */
  async updateUserProfile(userId, updates) {
    try {
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update user profile error:', error);
      return { data: null, error };
    }
  },

  /**
   * Reset password request
   * @param {string} email - User's email
   */
  async resetPassword(email) {
    try {
      // Use Supabase's resetPasswordForEmail which will send the email
      // The redirect URL should point to the reset password page
      // Use window.location.origin for production (works in both dev and prod)
      // Or use environment variable if available
      const baseUrl = import.meta.env.VITE_FRONTEND_URL || window.location.origin;
      const redirectUrl = `${baseUrl}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Reset password error:', error);
      return { error };
    }
  },

  /**
   * Update password
   * @param {string} newPassword - New password
   */
  async updatePassword(newPassword) {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Update password error:', error);
      return { error };
    }
  },

  /**
   * Listen to auth state changes
   * @param {function} callback - Callback function
   */
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChanged((event, session) => {
      callback(event, session);
    });
  },

  /**
   * Check if user is authenticated
   */
  async isAuthenticated() {
    const { user } = await this.getCurrentUser();
    return !!user;
  }
};

export default supabaseAuthService;
